/* ==========================================================================
   Admin API Functions
   Admin endpoints for pharmacy management, catalog, and providers
   Task F26: Enhanced error handling with session expiry events
   ========================================================================== */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import {
  adminSessionExpiredEvent,
  type SessionExpiredDetail,
} from '@/utils/sessionEvents'
import {
  getTwoFactorDeviceToken,
  setTwoFactorDeviceToken,
  clearTwoFactorDeviceToken,
} from '@/utils/twoFactorDevice'
import { isTwoFactorChallenge } from './types'
import type {
  ApiError,
  TokenResponse,
  LoginResponse,
  TwoFactorChallengeResponse,
  TwoFactorLoginRequest,
  TwoFactorStatusResponse,
  TwoFactorSetupResponse,
  MessageResponse,
  LoginRequest,
  SetPasswordRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  PharmacyResponse,
  PharmacyUpdate,
  CatalogProductCreate,
  CatalogProductUpdate,
  CatalogProductResponse,
  CatalogProductListResponse,
  Category,
  CategoryListResponse,
  PricingList,
  PricingListListResponse,
  PricingListCreate,
  PricingListUpdate,
  PricingListAssignmentResponse,
  ProviderInvite,
  ProviderUpdate,
  ProviderResponse,
  ProviderDetailResponse,
  ProviderListResponse,
  ProviderStatus,
  CatalogAssignmentUpdate,
  CatalogAssignmentResponse,
  ProviderSettingsResponse,
  ProviderSettingsUpdate,
  ProviderAddressCreate,
  ProviderAddressUpdate,
  ProviderAddressResponse,
  ProviderAddressListResponse,
  AccessRequestStatus,
  AccessRequestListResponse,
  AccessRequestResponse,
  AccessRequestDeny,
  CsvImportResult,
  PharmacyUserResponse,
  PharmacyUserListResponse,
  PharmacyUserInvite,
  PharmacyUserUpdate,
  ShipmentInfo,
} from './types'
import { ApiClientError } from './client'
import {
  keyFor,
  getCurrentSlug,
  ADMIN_PHARMACY_SLUG_KEY,
} from '@/utils/portalSlug'
import { safeGetItem, safeSetItem, safeRemoveItem } from '@/utils/safeStorage'

/* --------------------------------------------------------------------------
   Configuration
   -------------------------------------------------------------------------- */

const BASE_URL = import.meta.env.VITE_API_URL || '/api'

// Separate storage keys for admin auth (distinct from provider auth)
const ADMIN_TOKEN_KEY = 'tjm_admin_token'
const ADMIN_TOKEN_EXPIRY_KEY = 'tjm_admin_token_expires_at'
const ADMIN_REMEMBER_ME_KEY = 'tjm_admin_remember_me'

/* --------------------------------------------------------------------------
   Admin Token Management

   Like the provider token, admin auth is stored per pharmacy portal slug
   (`tjm_admin_token:<slug>`) so the same person can be signed into several
   admin portals at once (PORTAL-174). Each helper defaults to the slug in the
   current URL; pass an explicit slug for no-slug flows (global /admin/login,
   the Landing page). `ADMIN_PHARMACY_SLUG_KEY` stays a single global hint
   naming the last verified admin portal, for the no-slug pages.
   -------------------------------------------------------------------------- */

export function getAdminToken(slug?: string | null): string | null {
  return safeGetItem(keyFor(ADMIN_TOKEN_KEY, slug))
}

export function setAdminToken(token: string, slug?: string | null): void {
  safeSetItem(keyFor(ADMIN_TOKEN_KEY, slug), token)
}

export function clearAdminToken(slug?: string | null): void {
  safeRemoveItem(keyFor(ADMIN_TOKEN_KEY, slug))
}

export function getAdminTokenExpiry(slug?: string | null): Date | null {
  const expiry = safeGetItem(keyFor(ADMIN_TOKEN_EXPIRY_KEY, slug))
  if (!expiry) return null
  return new Date(expiry)
}

export function setAdminTokenExpiry(expiresAt: string, slug?: string | null): void {
  safeSetItem(keyFor(ADMIN_TOKEN_EXPIRY_KEY, slug), expiresAt)
}

export function clearAdminTokenExpiry(slug?: string | null): void {
  safeRemoveItem(keyFor(ADMIN_TOKEN_EXPIRY_KEY, slug))
}

export function getAdminRememberMe(slug?: string | null): boolean {
  return safeGetItem(keyFor(ADMIN_REMEMBER_ME_KEY, slug)) === 'true'
}

export function setAdminRememberMe(remember: boolean, slug?: string | null): void {
  if (remember) {
    safeSetItem(keyFor(ADMIN_REMEMBER_ME_KEY, slug), 'true')
  } else {
    safeRemoveItem(keyFor(ADMIN_REMEMBER_ME_KEY, slug))
  }
}

export function clearAdminRememberMe(slug?: string | null): void {
  safeRemoveItem(keyFor(ADMIN_REMEMBER_ME_KEY, slug))
}

/**
 * Check if the stored admin token is expired (defaults to the current portal slug).
 */
export function isAdminTokenExpired(slug?: string | null): boolean {
  const expiry = getAdminTokenExpiry(slug)
  if (!expiry) return true
  // Add 30 second buffer to account for network latency
  return new Date() >= new Date(expiry.getTime() - 30000)
}

/**
 * Clear all admin auth data for one portal (defaults to the current portal slug).
 *
 * The global "last admin portal" hint is dropped only when it points at the
 * portal being cleared: another portal's session may still be live in this
 * browser, and the Landing page should keep linking to it.
 */
export function clearAdminAuth(slug?: string | null): void {
  const resolved = slug === undefined ? getCurrentSlug() : slug
  clearAdminToken(resolved)
  clearAdminTokenExpiry(resolved)
  clearAdminRememberMe(resolved)
  const hint = safeGetItem(ADMIN_PHARMACY_SLUG_KEY)
  if (
    resolved !== null &&
    hint !== null &&
    hint.toLowerCase() === resolved.toLowerCase()
  ) {
    safeRemoveItem(ADMIN_PHARMACY_SLUG_KEY)
  }
}

/**
 * The portal whose admin session a request acts for: the portal in the URL, or
 * on the global no-slug pages (Landing, /admin/login) the last verified admin
 * portal. For single-portal admins this is exactly what the old global key did.
 */
function resolveAdminSlug(): string | null {
  return getCurrentSlug() ?? safeGetItem(ADMIN_PHARMACY_SLUG_KEY)
}

/* --------------------------------------------------------------------------
   Error Handling
   -------------------------------------------------------------------------- */

/**
 * Extract user-friendly message from API error response.
 */
function extractApiMessage(data: ApiError | null): string | null {
  if (!data) return null

  // Handle string detail (simple error message from API)
  if (typeof data.detail === 'string') {
    return data.detail
  }

  // Handle validation error array
  if (Array.isArray(data.detail) && data.detail.length > 0) {
    return data.detail.map((err) => err.msg).join(', ')
  }

  return null
}

/**
 * Explain a 401 to the session-expired modal when we know the cause. Anything else
 * (expired, revoked or malformed token) returns undefined and the modal keeps its
 * friendly default plus the normal re-login form.
 *
 * The stale-role detail is already good user-facing copy, so it passes through. The
 * disabled detail is terse ("Account has been disabled") and shared with the
 * provider and super-admin dependencies, so the admin-facing sentence is written
 * here rather than changing a backend string other call sites and tests depend on.
 * Disabled is also `terminal`: no password can work until an admin re-enables the
 * account, so the modal must not offer a login form the user can never satisfy.
 */
function explainAdminUnauthorized(
  detail: unknown
): SessionExpiredDetail | undefined {
  if (typeof detail !== 'string') return undefined
  if (detail.includes('access has changed')) return { message: detail }
  if (detail.includes('has been disabled')) {
    return {
      message: 'Your account has been disabled. Please contact your administrator.',
      terminal: true,
    }
  }
  return undefined
}

/**
 * Transform axios error into consistent ApiClientError.
 */
function transformError(error: AxiosError<ApiError>): ApiClientError {
  const status = error.response?.status || 0
  const data = error.response?.data || null
  const isValidationError = status === 422

  const apiMessage = extractApiMessage(data)

  let message: string
  if (apiMessage) {
    message = apiMessage
  } else {
    switch (status) {
      case 400:
        message = 'Bad request'
        break
      case 401:
        message = 'Invalid credentials'
        break
      case 403:
        message = 'Access denied'
        break
      case 404:
        message = 'Resource not found'
        break
      case 422:
        message = 'Please check your input and try again'
        break
      case 429:
        message = 'Too many requests. Please try again later.'
        break
      case 500:
        message = 'Server error. Please try again later.'
        break
      case 0:
        message = 'Unable to connect to server. Please check your connection.'
        break
      default:
        message = error.message || 'An unexpected error occurred'
    }
  }

  return new ApiClientError(message, status, data, { isValidationError })
}

/* --------------------------------------------------------------------------
   Admin Axios Instance
   Separate from provider client, uses admin token
   -------------------------------------------------------------------------- */

const adminClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// Track if we've already handled admin session expiry to prevent multiple events
let hasDispatchedAdminSessionExpired = false

/**
 * Request config carrying the portal slug whose admin token was attached, so a
 * 401 clears that portal's session rather than re-resolving the slug when the
 * response arrives: the URL or the landing-page hint can change while the
 * request is in flight (navigating to another portal, or another tab logging
 * in), and re-resolving then would clear the wrong portal's session.
 */
type AdminRequestConfig = InternalAxiosRequestConfig & {
  adminSlug?: string | null
}

// Request interceptor - attach the admin JWT token of the portal this request
// acts for (see resolveAdminSlug). A caller that already set Authorization
// explicitly (adminLogout revoking a specific portal's token) wins: sessions
// are per portal now, so overwriting it could revoke a different portal's
// session than the one being cleared locally.
adminClient.interceptors.request.use(
  (config: AdminRequestConfig) => {
    if (config.headers && !config.headers.Authorization) {
      const slug = resolveAdminSlug()
      const token = getAdminToken(slug)
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
        config.adminSlug = slug
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - handle 401 and error transformation
// Task F26: Now dispatches admin session expired event instead of hard redirect
adminClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error: AxiosError<ApiError>) => {
    const currentPath = window.location.pathname

    // Check if we're on an admin auth page
    const isAdminAuthPage =
      currentPath.includes('/admin/login') ||
      currentPath.includes('/admin/set-password') ||
      currentPath.includes('/admin/accept-invitation') ||
      currentPath.includes('/admin/reset-password') ||
      currentPath.includes('/admin/forgot-password')

    // A 401 from an auth call is that call failing (bad password, dead reset token)
    // — not the session dying. Without this, a failed re-login inside the session-
    // expired modal re-dispatches the event, which resets the modal and erases the
    // error it is trying to show. The isAdminAuthPage check above cannot catch it:
    // the browser is on the protected page the modal is covering, not on an auth
    // page. The exception is the 2FA settings routes (/admin/auth/two-factor*,
    // PORTAL-187): they need a live session, so a 401 there IS the session dying.
    // The login step /admin/auth/login/two-factor does not match that substring.
    const url = error.config?.url ?? ''
    const isAuthRequest =
      url.includes('/admin/auth/') && !url.includes('/admin/auth/two-factor')

    // Handle 401 Unauthorized - dispatch admin session expired event
    if (
      error.response?.status === 401 &&
      !hasDispatchedAdminSessionExpired &&
      !isAdminAuthPage &&
      !isAuthRequest
    ) {
      hasDispatchedAdminSessionExpired = true
      // Clear the session the request interceptor actually attached (recorded on
      // the request config); only fall back to resolving now if none was recorded.
      const requestSlug = (error.config as AdminRequestConfig | undefined)
        ?.adminSlug
      clearAdminAuth(requestSlug === undefined ? resolveAdminSlug() : requestSlug)

      // Dispatch admin session expired event so the modal can show
      setTimeout(() => {
        adminSessionExpiredEvent.dispatch(
          explainAdminUnauthorized(error.response?.data?.detail)
        )
        // Reset flag after a short delay to allow re-triggering if needed
        setTimeout(() => {
          hasDispatchedAdminSessionExpired = false
        }, 1000)
      }, 0)
    }

    const apiError = transformError(error)
    return Promise.reject(apiError)
  }
)

/* --------------------------------------------------------------------------
   Admin Auth API Functions
   -------------------------------------------------------------------------- */

function storeAdminSession(
  data: LoginResponse,
  requestSlug: string | undefined,
  rememberMe: boolean
): void {
  // Store under the portal the backend authenticated against, not the URL: the
  // global /admin/login page has no slug and then navigates to
  // `/rx/<portal_slug>/admin/...`, which reads the per-slug key. `||` (not `??`)
  // so a blank slug also falls through, see `withPortalSlug`.
  const slug = data.portal_slug || requestSlug || getCurrentSlug()
  setAdminToken(data.access_token, slug)
  setAdminTokenExpiry(data.expires_at, slug)
  setAdminRememberMe(rememberMe, slug)
}

/**
 * Admin login with email and password.
 * Returns token and portal_slug for redirect. When the account has 2FA on
 * (PORTAL-187) nothing is stored and the challenge is returned instead; finish
 * with `adminLoginTwoFactor`.
 */
export async function adminLogin(
  request: LoginRequest
): Promise<LoginResponse | TwoFactorChallengeResponse> {
  const response = await adminClient.post<LoginResponse | TwoFactorChallengeResponse>(
    '/admin/auth/login',
    {
      ...request,
      two_factor_device_token:
        request.two_factor_device_token ??
        getTwoFactorDeviceToken(
          'admin',
          request.email,
          request.portal_slug || getCurrentSlug()
        ),
    }
  )
  if (isTwoFactorChallenge(response.data)) {
    return response.data
  }

  storeAdminSession(response.data, request.portal_slug, request.remember_me ?? false)

  return response.data
}

/**
 * Second admin login step: the challenge pass plus the authenticator code
 * (PORTAL-187). Stores the session like `adminLogin`, and the "remember this
 * device" token if issued.
 */
export async function adminLoginTwoFactor(
  request: TwoFactorLoginRequest,
  email: string,
  rememberMe: boolean,
  portalSlug?: string
): Promise<LoginResponse> {
  const response = await adminClient.post<LoginResponse>(
    '/admin/auth/login/two-factor',
    request
  )
  storeAdminSession(response.data, portalSlug, rememberMe)
  setTwoFactorDeviceToken(
    'admin',
    email,
    response.data.two_factor_device_token,
    response.data.portal_slug || portalSlug || getCurrentSlug()
  )
  return response.data
}

/**
 * Admin logout for one portal (defaults to the current portal slug).
 * Task F32: Sends Authorization header for server-side token revocation.
 */
export async function adminLogout(slug?: string | null): Promise<void> {
  // Capture token before clearing - needed for server-side revocation
  const token = getAdminToken(slug)

  try {
    // Only call the API if we have a token to revoke
    if (token) {
      await adminClient.post('/admin/auth/logout', null, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    }
  } catch (error) {
    // Ignore errors on logout - we'll clear local data anyway
    // This ensures logout completes even if the server is unreachable
    if (!(error instanceof ApiClientError && error.status === 401)) {
      console.warn('Admin logout API call failed:', error)
    }
  } finally {
    // Always clear local auth data regardless of API response
    clearAdminAuth(slug)
  }
}

/**
 * Set initial password from admin invitation token.
 */
export async function adminSetPassword(
  request: SetPasswordRequest
): Promise<TokenResponse> {
  const response = await adminClient.post<TokenResponse>(
    '/admin/auth/set-password',
    request
  )

  // Store admin token and expiry
  setAdminToken(response.data.access_token)
  setAdminTokenExpiry(response.data.expires_at)

  return response.data
}

/**
 * Request admin password reset email.
 */
export async function adminForgotPassword(
  request: ForgotPasswordRequest
): Promise<MessageResponse> {
  const response = await adminClient.post<MessageResponse>(
    '/admin/auth/forgot-password',
    request
  )
  return response.data
}

/**
 * Reset admin password with token from email.
 * With 2FA on (PORTAL-187) the backend returns a challenge instead of a token:
 * nothing is stored and the caller sends the user to sign in.
 */
export async function adminResetPassword(
  request: ResetPasswordRequest
): Promise<TokenResponse | TwoFactorChallengeResponse> {
  const response = await adminClient.post<TokenResponse | TwoFactorChallengeResponse>(
    '/admin/auth/reset-password',
    request
  )
  if (isTwoFactorChallenge(response.data)) {
    return response.data
  }

  // Store admin token and expiry
  setAdminToken(response.data.access_token)
  setAdminTokenExpiry(response.data.expires_at)

  return response.data
}

/* --------------------------------------------------------------------------
   Two-factor settings for the signed-in pharmacy user (PORTAL-187)
   -------------------------------------------------------------------------- */

export async function getAdminTwoFactorStatus(): Promise<TwoFactorStatusResponse> {
  const response = await adminClient.get<TwoFactorStatusResponse>('/admin/auth/two-factor')
  return response.data
}

export async function startAdminTwoFactorSetup(): Promise<TwoFactorSetupResponse> {
  const response = await adminClient.post<TwoFactorSetupResponse>(
    '/admin/auth/two-factor/setup'
  )
  return response.data
}

/** Turn 2FA on. This browser is not remembered: that is opt-in at the login step. */
export async function enableAdminTwoFactor(code: string): Promise<TwoFactorStatusResponse> {
  const response = await adminClient.post<TwoFactorStatusResponse>(
    '/admin/auth/two-factor/enable',
    { code }
  )
  return response.data
}

export async function disableAdminTwoFactor(
  code: string,
  email: string
): Promise<TwoFactorStatusResponse> {
  const response = await adminClient.post<TwoFactorStatusResponse>(
    '/admin/auth/two-factor/disable',
    { code }
  )
  clearTwoFactorDeviceToken('admin', email, resolveAdminSlug())
  return response.data
}

/* --------------------------------------------------------------------------
   Pharmacy API Functions
   -------------------------------------------------------------------------- */

/**
 * Get pharmacy details by slug.
 */
export async function getPharmacy(slug: string): Promise<PharmacyResponse> {
  const response = await adminClient.get<PharmacyResponse>(
    `/pharmacies/${slug}`
  )
  return response.data
}

/**
 * Update pharmacy settings.
 */
export async function updatePharmacy(
  slug: string,
  data: PharmacyUpdate
): Promise<PharmacyResponse> {
  const response = await adminClient.put<PharmacyResponse>(
    `/pharmacies/${slug}`,
    data
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Pharmacy Logo Upload
   -------------------------------------------------------------------------- */

/**
 * Upload a logo image for a pharmacy.
 * Returns the S3 key and filename.
 */
export async function uploadPharmacyLogo(
  slug: string,
  file: File,
): Promise<{ file_url: string; file_name: string }> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await adminClient.post<{ file_url: string; file_name: string }>(
    `/pharmacies/${slug}/upload-logo`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Catalog API Functions (Admin)
   -------------------------------------------------------------------------- */

export interface ListCatalogOptions {
  is_active?: boolean
}

/**
 * List all catalog products for a pharmacy.
 * Admin view includes inactive products.
 */
export async function listCatalog(
  slug: string,
  options?: ListCatalogOptions
): Promise<CatalogProductListResponse> {
  const params = new URLSearchParams()
  if (options?.is_active !== undefined) {
    params.set('is_active', String(options.is_active))
  }

  const url = `/pharmacies/${slug}/catalog${params.toString() ? `?${params.toString()}` : ''}`
  const response = await adminClient.get<CatalogProductListResponse>(url)
  return response.data
}

/**
 * Create a new catalog product.
 */
export async function createProduct(
  slug: string,
  data: CatalogProductCreate
): Promise<CatalogProductResponse> {
  const response = await adminClient.post<CatalogProductResponse>(
    `/pharmacies/${slug}/catalog`,
    data
  )
  return response.data
}

/**
 * Get a single catalog product by ID.
 */
export async function getProduct(
  slug: string,
  productId: string
): Promise<CatalogProductResponse> {
  const response = await adminClient.get<CatalogProductResponse>(
    `/pharmacies/${slug}/catalog/${productId}`
  )
  return response.data
}

/**
 * Update a catalog product.
 */
export async function updateProduct(
  slug: string,
  productId: string,
  data: CatalogProductUpdate
): Promise<CatalogProductResponse> {
  const response = await adminClient.put<CatalogProductResponse>(
    `/pharmacies/${slug}/catalog/${productId}`,
    data
  )
  return response.data
}

/**
 * Delete (deactivate) a catalog product.
 * This is a soft delete - sets is_active to false.
 */
export async function deleteProduct(
  slug: string,
  productId: string
): Promise<CatalogProductResponse> {
  const response = await adminClient.delete<CatalogProductResponse>(
    `/pharmacies/${slug}/catalog/${productId}`
  )
  return response.data
}

/**
 * Permanently delete one or more catalog products (PORTAL-127).
 * All or nothing. This cannot be undone.
 */
export async function bulkDeleteProducts(
  slug: string,
  productIds: string[]
): Promise<void> {
  await adminClient.post(`/pharmacies/${slug}/catalog/bulk-delete`, {
    product_ids: productIds,
  })
}

/**
 * List all categories for a pharmacy, with product counts (PORTAL-77).
 */
export async function listCategories(slug: string): Promise<CategoryListResponse> {
  const response = await adminClient.get<CategoryListResponse>(
    `/pharmacies/${slug}/categories`
  )
  return response.data
}

/**
 * Create a new category.
 */
export async function createCategory(slug: string, name: string): Promise<Category> {
  const response = await adminClient.post<Category>(
    `/pharmacies/${slug}/categories`,
    { name }
  )
  return response.data
}

/**
 * Rename a category. Products keep the category under its new name.
 */
export async function renameCategory(
  slug: string,
  categoryId: string,
  name: string
): Promise<Category> {
  const response = await adminClient.patch<Category>(
    `/pharmacies/${slug}/categories/${categoryId}`,
    { name }
  )
  return response.data
}

/**
 * Delete a category. Products are untagged, never deleted.
 */
export async function deleteCategory(slug: string, categoryId: string): Promise<void> {
  await adminClient.delete(`/pharmacies/${slug}/categories/${categoryId}`)
}

/**
 * List pricing lists with entry and provider counts (PORTAL-204).
 */
export async function listPricingLists(slug: string): Promise<PricingListListResponse> {
  const response = await adminClient.get<PricingListListResponse>(
    `/pharmacies/${slug}/pricing-lists`
  )
  return response.data
}

/**
 * Create a pricing list: blank, a copy of another list, or a percent off
 * every default price.
 */
export async function createPricingList(
  slug: string,
  data: PricingListCreate
): Promise<PricingList> {
  const response = await adminClient.post<PricingList>(
    `/pharmacies/${slug}/pricing-lists`,
    data
  )
  return response.data
}

/**
 * Rename a pricing list or edit its description. Prices and providers stay.
 */
export async function updatePricingList(
  slug: string,
  listId: string,
  data: PricingListUpdate
): Promise<PricingList> {
  const response = await adminClient.patch<PricingList>(
    `/pharmacies/${slug}/pricing-lists/${listId}`,
    data
  )
  return response.data
}

/**
 * Delete a pricing list. Its providers move to `moveProvidersTo`, or to the
 * default price when null.
 */
export async function deletePricingList(
  slug: string,
  listId: string,
  moveProvidersTo: string | null
): Promise<void> {
  await adminClient.delete(`/pharmacies/${slug}/pricing-lists/${listId}`, {
    params: moveProvidersTo ? { move_providers_to: moveProvidersTo } : undefined,
  })
}

/**
 * Put providers on a pricing list, or on the default price when null.
 */
export async function assignPricingList(
  slug: string,
  providerIds: string[],
  pricingListId: string | null
): Promise<PricingListAssignmentResponse> {
  const response = await adminClient.put<PricingListAssignmentResponse>(
    `/pharmacies/${slug}/pricing-lists/assignments`,
    { provider_ids: providerIds, pricing_list_id: pricingListId }
  )
  return response.data
}

/**
 * Download CSV catalog template for bulk import.
 */
export async function downloadCatalogCsvTemplate(slug: string): Promise<Blob> {
  const response = await adminClient.get(`/pharmacies/${slug}/catalog/csv-template`, {
    responseType: 'blob',
  })
  return response.data
}

/**
 * Export the full catalog as CSV in the import format (PORTAL-58).
 * Round-trips: edit the file and re-upload with mode "upsert".
 */
export async function exportCatalogCsv(slug: string): Promise<Blob> {
  const response = await adminClient.get(`/pharmacies/${slug}/catalog/export-csv`, {
    responseType: 'blob',
  })
  return response.data
}

/**
 * Import catalog products from a CSV file.
 * mode "create" (default) rejects existing product codes; "upsert" updates them.
 * With dryRun, nothing is written — the result previews what an apply would do.
 */
export async function importCatalogCsv(
  slug: string,
  file: File,
  mode: 'create' | 'upsert' = 'create',
  dryRun = false
): Promise<CsvImportResult> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await adminClient.post<CsvImportResult>(
    `/pharmacies/${slug}/catalog/import-csv?mode=${mode}${dryRun ? '&dry_run=true' : ''}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Provider Management API Functions
   -------------------------------------------------------------------------- */

export interface ListProvidersOptions {
  status?: ProviderStatus
}

/**
 * List all providers for a pharmacy.
 */
export async function listProviders(
  slug: string,
  options?: ListProvidersOptions
): Promise<ProviderListResponse> {
  const params = new URLSearchParams()
  if (options?.status) {
    params.set('status', options.status)
  }

  const url = `/pharmacies/${slug}/providers${params.toString() ? `?${params.toString()}` : ''}`
  const response = await adminClient.get<ProviderListResponse>(url)
  return response.data
}

/**
 * Invite a new provider.
 * Sends invitation email with password setup link.
 */
export async function inviteProvider(
  slug: string,
  data: ProviderInvite
): Promise<ProviderResponse> {
  const response = await adminClient.post<ProviderResponse>(
    `/pharmacies/${slug}/providers`,
    data
  )
  return response.data
}

/**
 * Get a single provider's full details by ID (PORTAL-74).
 */
export async function getProvider(
  slug: string,
  providerId: string
): Promise<ProviderDetailResponse> {
  const response = await adminClient.get<ProviderDetailResponse>(
    `/pharmacies/${slug}/providers/${providerId}`
  )
  return response.data
}

/**
 * Update a provider.
 */
export async function updateProvider(
  slug: string,
  providerId: string,
  data: ProviderUpdate
): Promise<ProviderResponse> {
  const response = await adminClient.put<ProviderResponse>(
    `/pharmacies/${slug}/providers/${providerId}`,
    data
  )
  return response.data
}

/**
 * Delete a provider.
 * This is a permanent delete.
 */
export async function deleteProvider(
  slug: string,
  providerId: string
): Promise<void> {
  await adminClient.delete(`/pharmacies/${slug}/providers/${providerId}`)
}

/**
 * Resend invitation email to a provider.
 * Only works for providers with status 'invited'.
 */
export async function resendProviderInvite(
  slug: string,
  providerId: string
): Promise<ProviderResponse> {
  const response = await adminClient.post<ProviderResponse>(
    `/pharmacies/${slug}/providers/${providerId}/resend-invite`
  )
  return response.data
}

/**
 * Turn off a provider's 2FA after they lose their phone (PORTAL-187).
 * Admins and Managers only.
 */
export async function resetProviderTwoFactor(
  slug: string,
  providerId: string
): Promise<void> {
  await adminClient.post(`/pharmacies/${slug}/providers/${providerId}/reset-two-factor`)
}

/* --------------------------------------------------------------------------
   Pharmacy team members (PORTAL-94)
   -------------------------------------------------------------------------- */

/** List the pharmacy's team members (Admin/Manager only). */
export async function listPharmacyUsers(
  slug: string
): Promise<PharmacyUserListResponse> {
  const response = await adminClient.get<PharmacyUserListResponse>(
    `/pharmacies/${slug}/users`
  )
  return response.data
}

/** Invite a new team member (sends an invitation email). */
export async function invitePharmacyUser(
  slug: string,
  data: PharmacyUserInvite
): Promise<PharmacyUserResponse> {
  const response = await adminClient.post<PharmacyUserResponse>(
    `/pharmacies/${slug}/users`,
    data
  )
  return response.data
}

/** Update a team member's name / role / status. */
export async function updatePharmacyUser(
  slug: string,
  userId: string,
  data: PharmacyUserUpdate
): Promise<PharmacyUserResponse> {
  const response = await adminClient.put<PharmacyUserResponse>(
    `/pharmacies/${slug}/users/${userId}`,
    data
  )
  return response.data
}

/** Resend the invitation email for a not-yet-activated team member. */
export async function resendPharmacyUserInvite(
  slug: string,
  userId: string
): Promise<void> {
  await adminClient.post(`/pharmacies/${slug}/users/${userId}/resend-invite`)
}

/** Turn off a team member's 2FA after they lose their phone (PORTAL-187). */
export async function resetPharmacyUserTwoFactor(
  slug: string,
  userId: string
): Promise<void> {
  await adminClient.post(`/pharmacies/${slug}/users/${userId}/reset-two-factor`)
}

/** Remove a team member. */
export async function deletePharmacyUser(
  slug: string,
  userId: string
): Promise<void> {
  await adminClient.delete(`/pharmacies/${slug}/users/${userId}`)
}

/**
 * Get catalog assignments for a provider.
 */
export async function getProviderCatalogAssignments(
  slug: string,
  providerId: string
): Promise<CatalogAssignmentResponse> {
  const response = await adminClient.get<CatalogAssignmentResponse>(
    `/pharmacies/${slug}/providers/${providerId}/catalog-assignments`
  )
  return response.data
}

/**
 * Update catalog assignments for a provider.
 * Send empty product_ids array to restore default (all products).
 */
export async function updateProviderCatalogAssignments(
  slug: string,
  providerId: string,
  data: CatalogAssignmentUpdate
): Promise<CatalogAssignmentResponse> {
  const response = await adminClient.put<CatalogAssignmentResponse>(
    `/pharmacies/${slug}/providers/${providerId}/catalog-assignments`,
    data
  )
  return response.data
}

/**
 * Get a provider's per-provider setting overrides, the pharmacy defaults, and
 * the resolved effective values (PORTAL-72).
 */
export async function getProviderSettings(
  slug: string,
  providerId: string
): Promise<ProviderSettingsResponse> {
  const response = await adminClient.get<ProviderSettingsResponse>(
    `/pharmacies/${slug}/providers/${providerId}/settings`
  )
  return response.data
}

/**
 * Update a provider's setting overrides. Send null for a field to clear the
 * override (inherit the pharmacy default); omit a field to leave it unchanged.
 */
export async function updateProviderSettings(
  slug: string,
  providerId: string,
  data: ProviderSettingsUpdate
): Promise<ProviderSettingsResponse> {
  const response = await adminClient.put<ProviderSettingsResponse>(
    `/pharmacies/${slug}/providers/${providerId}/settings`,
    data
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Provider Practice Address Management (admin — issues #140 / #180)
   -------------------------------------------------------------------------- */

/**
 * List a provider's practice addresses (pharmacy-admin view).
 */
export async function adminListProviderAddresses(
  slug: string,
  providerId: string
): Promise<ProviderAddressListResponse> {
  const response = await adminClient.get<ProviderAddressListResponse>(
    `/pharmacies/${slug}/providers/${providerId}/addresses`
  )
  return response.data
}

/**
 * Create a practice address for a provider (pharmacy-admin).
 */
export async function adminCreateProviderAddress(
  slug: string,
  providerId: string,
  data: ProviderAddressCreate
): Promise<ProviderAddressResponse> {
  const response = await adminClient.post<ProviderAddressResponse>(
    `/pharmacies/${slug}/providers/${providerId}/addresses`,
    data
  )
  return response.data
}

/**
 * Update a provider practice address (pharmacy-admin).
 */
export async function adminUpdateProviderAddress(
  slug: string,
  providerId: string,
  addressId: string,
  data: ProviderAddressUpdate
): Promise<ProviderAddressResponse> {
  const response = await adminClient.put<ProviderAddressResponse>(
    `/pharmacies/${slug}/providers/${providerId}/addresses/${addressId}`,
    data
  )
  return response.data
}

/**
 * Delete a provider practice address (pharmacy-admin).
 */
export async function adminDeleteProviderAddress(
  slug: string,
  providerId: string,
  addressId: string
): Promise<void> {
  await adminClient.delete(
    `/pharmacies/${slug}/providers/${providerId}/addresses/${addressId}`
  )
}

/* --------------------------------------------------------------------------
   Prescription Management API Functions
   -------------------------------------------------------------------------- */

export interface ListPrescriptionsOptions {
  status?: string
  provider_id?: string
  date_from?: string
  date_to?: string
  search?: string
  limit?: number
  offset?: number
}

export interface PrescriptionListProvider {
  id: string
  name: string | null
  email: string
  npi: string | null
}

export interface PrescriptionListItem {
  id: string
  reference_id: string
  status: string
  drug_name: string
  product_code: string
  patient_type: string
  patient_first_name: string | null
  patient_last_name: string | null
  patient_dob: string | null
  animal_name: string | null
  animal_species: string | null
  owner_name: string | null
  has_pdf: boolean
  submitted_at: string
  created_at: string
  provider: PrescriptionListProvider | null
  clinic_name: string | null
  fax_attempt_count: number
  latest_fax_status: 'pending' | 'success' | 'failed' | null
  latest_fax_error: string | null
  // Null unless the pharmacy has shipment tracking enabled AND the package has
  // shipped (PORTAL-146). Rendered on the Faxes page (PORTAL-186).
  shipment?: ShipmentInfo | null
  // Admin-typed tracking number, independent of `shipment`. Null unless the
  // pharmacy has manual tracking enabled (PORTAL-192).
  manual_tracking_number?: string | null
}

export interface PrescriptionListResponse {
  prescriptions: PrescriptionListItem[]
  total: number
  limit: number
  offset: number
}

/**
 * List prescriptions for a pharmacy.
 * Used for dashboard stats and prescription history.
 */
export async function listPrescriptions(
  slug: string,
  options?: ListPrescriptionsOptions
): Promise<PrescriptionListResponse> {
  const params = new URLSearchParams()
  if (options?.status) {
    params.set('status', options.status)
  }
  if (options?.provider_id) {
    params.set('provider_id', options.provider_id)
  }
  if (options?.date_from) {
    params.set('date_from', options.date_from)
  }
  if (options?.date_to) {
    params.set('date_to', options.date_to)
  }
  if (options?.search) {
    params.set('search', options.search)
  }
  if (options?.limit !== undefined) {
    params.set('limit', String(options.limit))
  }
  if (options?.offset !== undefined) {
    params.set('offset', String(options.offset))
  }

  const url = `/pharmacies/${slug}/prescriptions${params.toString() ? `?${params.toString()}` : ''}`
  const response = await adminClient.get<PrescriptionListResponse>(url)
  return response.data
}

/**
 * Fetch a pre-signed PDF URL for a prescription.
 * Pharmacy admin uses the detail endpoint (which returns pdf_url) since there
 * is no dedicated /pdf route on the pharmacy-side API.
 */
export async function getPrescriptionPdfUrl(
  slug: string,
  prescriptionId: string
): Promise<string | null> {
  const response = await adminClient.get<{ pdf_url: string | null }>(
    `/pharmacies/${slug}/prescriptions/${prescriptionId}`
  )
  return response.data.pdf_url ?? null
}

export interface RetryFaxResponse {
  message: string
  reference_id: string
  attempt_number: number
  status: string
  fax_id: string | null
}

/**
 * Manually retry fax delivery for a prescription.
 * The endpoint is keyed by prescription ID and pharmacy-scoped server-side
 * (it verifies the prescription belongs to the authenticated admin's pharmacy).
 */
export async function retryFax(
  prescriptionId: string
): Promise<RetryFaxResponse> {
  const response = await adminClient.post<RetryFaxResponse>(
    `/prescriptions/${prescriptionId}/retry-fax`
  )
  return response.data
}

export interface TrackingNumberUpdateResponse {
  id: string
  reference_id: string
  tracking_number: string | null
}

/**
 * Set or clear the tracking number for a fax (PORTAL-192). Pass null or an
 * empty string to clear. Only available to manual-tracking pharmacies.
 */
export async function updateTrackingNumber(
  slug: string,
  prescriptionId: string,
  trackingNumber: string | null
): Promise<TrackingNumberUpdateResponse> {
  const response = await adminClient.put<TrackingNumberUpdateResponse>(
    `/pharmacies/${slug}/prescriptions/${prescriptionId}/tracking-number`,
    { tracking_number: trackingNumber }
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Access Request Management API Functions
   -------------------------------------------------------------------------- */

export interface ListAccessRequestsOptions {
  status?: AccessRequestStatus
  limit?: number
  offset?: number
}

/**
 * List access requests for a pharmacy.
 */
export async function listAccessRequests(
  slug: string,
  options?: ListAccessRequestsOptions
): Promise<AccessRequestListResponse> {
  const params = new URLSearchParams()
  if (options?.status) {
    params.set('status', options.status)
  }
  if (options?.limit !== undefined) {
    params.set('limit', String(options.limit))
  }
  if (options?.offset !== undefined) {
    params.set('offset', String(options.offset))
  }

  const url = `/pharmacies/${slug}/access-requests${params.toString() ? `?${params.toString()}` : ''}`
  const response = await adminClient.get<AccessRequestListResponse>(url)
  return response.data
}

/**
 * Approve an access request.
 */
export async function approveAccessRequest(
  slug: string,
  requestId: string
): Promise<AccessRequestResponse> {
  const response = await adminClient.post<AccessRequestResponse>(
    `/pharmacies/${slug}/access-requests/${requestId}/approve`
  )
  return response.data
}

/**
 * Deny an access request.
 */
export async function denyAccessRequest(
  slug: string,
  requestId: string,
  data?: AccessRequestDeny
): Promise<AccessRequestResponse> {
  const response = await adminClient.post<AccessRequestResponse>(
    `/pharmacies/${slug}/access-requests/${requestId}/deny`,
    data || {}
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Billing API Functions
   -------------------------------------------------------------------------- */

export interface SubscriptionDetail {
  id: string
  tier: 'starter' | 'growth' | 'scale'
  status: 'active' | 'past_due' | 'canceled' | 'incomplete'
  billing_cycle: 'monthly' | 'annual'
  currency: 'usd' | 'cad'
  current_period_start: string | null
  current_period_end: string | null
  script_count: number
  script_limit: number
  overage_count: number
  pending_tier: string | null
  pending_billing_cycle: 'monthly' | 'annual' | null
  contact_email: string
  next_invoice_date: string | null
  price_amount_cents: number
  alert_80_sent: boolean
}

export interface PortalSessionResult {
  portal_url: string
}

/**
 * Get subscription details for a pharmacy.
 */
export async function getSubscription(
  slug: string
): Promise<SubscriptionDetail> {
  const response = await adminClient.get<SubscriptionDetail>(
    `/admin/billing/${slug}/subscription`
  )
  return response.data
}

/**
 * Create a Stripe Customer Portal session.
 * Returns a URL to redirect the user to.
 */
export async function createPortalSession(
  slug: string
): Promise<PortalSessionResult> {
  const response = await adminClient.post<PortalSessionResult>(
    `/admin/billing/${slug}/portal-session`
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Exports
   -------------------------------------------------------------------------- */

export { adminClient }

export default {
  // Auth
  adminLogin,
  adminLogout,
  adminSetPassword,
  adminForgotPassword,
  adminResetPassword,

  // Token management
  getAdminToken,
  setAdminToken,
  clearAdminToken,
  getAdminTokenExpiry,
  setAdminTokenExpiry,
  clearAdminTokenExpiry,
  getAdminRememberMe,
  setAdminRememberMe,
  clearAdminRememberMe,
  isAdminTokenExpired,
  clearAdminAuth,

  // Pharmacy
  getPharmacy,
  updatePharmacy,

  // Catalog
  listCatalog,
  createProduct,
  getProduct,
  updateProduct,
  deleteProduct,

  // Categories
  listCategories,
  createCategory,
  renameCategory,
  deleteCategory,

  // Pricing lists
  listPricingLists,
  createPricingList,
  updatePricingList,
  deletePricingList,
  assignPricingList,

  // Providers
  listProviders,
  inviteProvider,
  getProvider,
  updateProvider,
  deleteProvider,
  resendProviderInvite,
  getProviderCatalogAssignments,
  updateProviderCatalogAssignments,
  getProviderSettings,
  updateProviderSettings,

  // Pharmacy team members
  listPharmacyUsers,
  invitePharmacyUser,
  updatePharmacyUser,
  resendPharmacyUserInvite,
  deletePharmacyUser,

  // Prescriptions
  listPrescriptions,

  // Access Requests
  listAccessRequests,
  approveAccessRequest,
  denyAccessRequest,

  // Billing
  getSubscription,
  createPortalSession,
}
